import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { commonAttributes } from "./../common/commonAttributes";
import { RootState } from "../../app/store";
import { CompanyRoleType, DeviceSettingsDeviceObject, ProjectObject } from "../../types";
import { GetDateString } from "../utils/commonFunctions";

export interface CompanyState {
  userCompanyRoles: Array<UserCompanyObject>;
  companyProjects: Array<AllCompanyProjects>;
  showAllCompanyProjects: Array<CompanyProjectsState>;
  userCompanies: Array<UserCompanyObject>;
  companyDevices: Array<DeviceSettingsDeviceObject>;
  companyUsers: Array<UserCompanyObject>;
  companyRoles: Array<CompanyRoleType>;
  error: boolean;
  loading: boolean;
}

export type CompanyProjectsState = {
  companyId: number;
  showAllProjects: boolean;
}

export type AllCompanyProjects = {
  companyId: number;
  companyProjects: Array<ProjectObject>;
}

export type UserCompanyObject = {
  id: number;
  startDateTime: string;
  endDateTime: string;
  status: number;
  userId: string;
  userName: string;
  companyId: number;
  companyName: string;
  companyRoleId: number;
  companyRoleName: string;
};

export type UpdatedUserDTO = {
  id: number;
  username: string;
  companyId: number;
  companyRoleID: number;
  endDateTime: string;
};

export type NewUserDTO = {
  userName: string;
  companyId: number;
  companyRoleId: number;
  startDateTime: string;
}

const initialState: CompanyState = {
  userCompanyRoles: [],
  companyProjects: [],
  showAllCompanyProjects: [],
  userCompanies: [],
  companyDevices: [],
  companyUsers: [],
  companyRoles: [],
  error: false,
  loading: false
};

export const getCompanyProjects = createAsyncThunk(
  "SelectedCompany/getCompanyProjects", 
  async (companyId: number) => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Companies/${companyId}/Projects`);

    return response.data;
  }
);

export const getCompanyUsers = createAsyncThunk(
  "SelectedCompany/getCompanyUsers",
  async (companyId: number) => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Companies/${companyId}/Users`);

    return { data: response.data };
  }
);

export const addCompanyUser = createAsyncThunk(
  "SelectedCompany/addCompanyUser",
  async (params: { companyId: number, user: NewUserDTO }) => {
    const response = await axios.post(`${commonAttributes.apiUrl}/Companies/${params.companyId}/Users/Add`, params.user);

    return { data: response.data };
  }
);

export const removeCompanyUser = createAsyncThunk(
  "SelectedCompany/removeCompanyUser",
  async (user: UpdatedUserDTO) => {
    await axios.post(`${commonAttributes.apiUrl}/Companies/Users/${user.id}`, user);

    return { data: user};
  }
);

export const getCompanyRoles = createAsyncThunk(
  "SelectedCompany/getCompanyRoles",
  async () => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Companies/Roles`);

    return { data: response.data };
  }
);

export const getCompanyUserRights = createAsyncThunk(
  "SelectedCompany/getCompanyUserRights",
  async (projectId: string | undefined) => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Users/Companies`);

    return response.data;
  }
);

export const addCurrentUserInformation = createAsyncThunk(
  "SelectedCompany/addCurrentUserInformation",
  async () => {
    await axios.get(`${commonAttributes.apiUrl}/Users/Current/Add`);

    return {};
  }
);

export const getCompanyDevices = createAsyncThunk(
  "SelectedCompany/getCompanyDevices",
  async (companyId: number) => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Companies/${companyId}/Devices/ProjectsAndMeasurements`);

    return {data: response.data};
  }
);

export const getCompanyDevicesForProject = createAsyncThunk(
  "SelectedCompany/getCompanyDevicesForProject",
  async (params: {companyId: number, projectId: number}) => {
    const response = await axios.get(`${commonAttributes.apiUrl}/Companies/${params.companyId}/Projects/${params.projectId}Devices/ProjectsAndMeasurements`);

    return {data: response.data};
  }
);

export const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    toggleShowAllCompanyProjects: (state, action: PayloadAction<number>) => {
      const companyId = action.payload;
      const index = state.showAllCompanyProjects.findIndex(
        company => company.companyId === companyId
      );

      if (index >= 0) {
        let company = state.showAllCompanyProjects[index];

        state.showAllCompanyProjects[index] = { ...company, showAllProjects: !state.showAllCompanyProjects[index].showAllProjects };
      }else {
        state.showAllCompanyProjects.push({companyId: companyId, showAllProjects: false});
      }
    },
    clearCompanySliceError: state => {
      state.error = false;
    },
    clearCompanyDevices: state => {
      state.companyDevices = [];
    }
  },
  extraReducers: {
    // **********************************************************************************
    [getCompanyUserRights.pending.type]: (state, action) => {},
    [getCompanyUserRights.fulfilled.type]: (state, action) => {
      state.userCompanyRoles = action.payload;
    },
    [getCompanyUserRights.rejected.type]: (state, action) => {
      console.log("FAILED to get user rights");
      state.userCompanyRoles = [];
    },
    [getCompanyUsers.pending.type]: (state, action) => {

    },
    [getCompanyUsers.fulfilled.type]: (state, action) => {
      const companyUsers = action.payload.data;

      const usersData: Array<UserCompanyObject> = Array<UserCompanyObject>();

      companyUsers.forEach((user: UserCompanyObject) => {
        //console.log(user);

        if (user.endDateTime !== null) return;

        usersData.push({
          id: user.id,
          status: user.status,
          userId: user.userId,
          userName: user.userName,
          companyId: user.companyId,
          companyName: user.companyName,
          companyRoleId: user.companyRoleId,
          companyRoleName: user.companyRoleName,

          startDateTime: GetDateString(user.startDateTime, "DD-MM-YYYY"),
          endDateTime: GetDateString(user.endDateTime, "DD-MM-YYYY")
        });
      });
      state.companyUsers = usersData;
    },
    [getCompanyUsers.rejected.type]: (state, action) => {
      console.log("Failed to get company users");
    },
    [addCompanyUser.pending.type]: (state, action) => {},
    [addCompanyUser.fulfilled.type]: (state, action) => {},
    [addCompanyUser.rejected.type]: (state, action) => {
      console.log("Failed to add a company user");
    },
    [removeCompanyUser.pending.type]: (state, action) => {},
    [removeCompanyUser.fulfilled.type]: (state, action) => {
      const remainingUsers = state.companyUsers.filter(user => user.id !== action.payload.data.id);

      state.companyUsers = remainingUsers;
    },
    [removeCompanyUser.rejected.type]: (state, action) => {
      console.log("Failed to update company user(s)");
    },
    [getCompanyRoles.pending.type]: (state, action) => {},
    [getCompanyRoles.fulfilled.type]: (state, action) => {
      const data = action.payload.data;

      let companyRoles: Array<CompanyRoleType> = Array<CompanyRoleType>();

      data.map((role: CompanyRoleType) => companyRoles.push({ 
        id: role.id, 
        name: role.name 
      }));
      state.companyRoles = companyRoles;
    },
    [getCompanyRoles.rejected.type]: (state, action) => {
      console.log("Failed to get company roles");
    },
    // **********************************************************************************
    [addCurrentUserInformation.pending.type]: (state, action) => {},
    [addCurrentUserInformation.fulfilled.type]: (state, action) => {},
    [addCurrentUserInformation.rejected.type]: (state, action) => {
      console.log("addCurrentUserInformation rejected");
    },
    [getCompanyProjects.pending.type]: (state, action) => {},
    [getCompanyProjects.fulfilled.type]: (state, action: PayloadAction<ProjectObject[]>) => {
      const response = action.payload;
      if (response.length === 0) {
        return;
      }
      let companyId = response[0].ownerCompanyId;

      const activeProjects = action.payload.filter(project => project.status !== 5);

      const index = state.companyProjects.findIndex(
        company => company.companyId === companyId
      )
      if (index >= 0) {
        let company = state.companyProjects[index];
        state.companyProjects[index] = {...company, companyProjects: state.companyProjects[index].companyProjects = activeProjects};
      }else {
        state.companyProjects.push({companyId: companyId, companyProjects: activeProjects});
      }
    },
    [getCompanyProjects.rejected.type]: (state, action) => {
      console.log("getCompanyProjects rejected");
    },
    [getCompanyDevices.pending.type]: (state, action) => {
      state.loading = true;
    },
    [getCompanyDevices.fulfilled.type]: (state, action) => {
      state.companyDevices = action.payload.data;
      state.loading = false;
    },
    [getCompanyDevices.rejected.type]: (state, action) => {
      state.error = true;
      state.loading = false;
      console.log("Failed to get companyDevices");
    },
    [getCompanyDevicesForProject.pending.type]: (state, action) => {
      state.loading = true;
    },
    [getCompanyDevicesForProject.fulfilled.type]: (state, action) => {
      state.companyDevices = action.payload.data;
      state.loading = false;
    },
    [getCompanyDevicesForProject.rejected.type]: (state, action) => {
      state.error = true;
      state.loading = false;
      console.log("Failed to get companyDevices for this project");
    }
  }
});

export const getUserProjectManagerCompanies = (state: RootState) => {
  const selectedData: Array<UserCompanyObject> = state.company.userCompanyRoles.filter(
    x => x.companyRoleName === "ProjectManager"
  );

  return selectedData;
};
export const getUserProjectManagerAndAdminCompanies = (state: RootState) => {
  const selectedData: Array<UserCompanyObject> = state.company.userCompanyRoles.filter(
    x => x.companyRoleName === "ProjectManager" || x.companyRoleName === "Admin"
  );

  return selectedData;
};

export const getUserAdminAndFinancialCompanies = (state: RootState) => {
  const selectedData: Array<UserCompanyObject> = state.company.userCompanyRoles.filter(
    x => x.companyRoleName === "Admin" || x.companyRoleName === "FinancialManager"
  );

  return selectedData;
};

export const getUserAdminCompanies = (state: RootState) => {
  const selectedData: Array<UserCompanyObject> = state.company.userCompanyRoles.filter(
    x => x.companyRoleName === "Admin"
  );

  return selectedData;
};

export const getUserFinancialManagerCompanies = (state: RootState) => {
  const selectedData: Array<UserCompanyObject> = state.company.userCompanyRoles.filter(
    x => x.companyRoleName === "FinancialManager"
  );

  return selectedData;
};

export const { toggleShowAllCompanyProjects, clearCompanySliceError, clearCompanyDevices } = companySlice.actions;

export default companySlice.reducer;
